import { useEffect, useState, useCallback } from 'preact/hooks'
import { RANGE_INDICATOR, isPriceFilterField, formatPriceValue, parseActiveRange } from 'libs/athos/components/collection/filters/FilterUtils'
import debounce from 'lodash.debounce'
import { formatValue } from 'libs/athos/components/collection/filters/FilterHooks'

const isRealRange = (range) => Array.isArray(range) && range.length >= 2 && (range[0] !== 0 || range[1] !== 0)

/**
 * Resolve the slider's min/max. Prefers the first-load snapshot (`filter`) over
 * the live facet (`fetchedFilter`): once a range filter is actively applied,
 * the live facet's own range can narrow to the current selection, which would
 * otherwise collapse the track instead of showing the full available range.
 * Returns null when neither carries a real (non `[0, 0]`) range, so the
 * caller can hide the control rather than render a dead `$0 / $0` slider.
 */
export function getSliderBounds(filter, fetchedFilter) {
  if (isRealRange(filter?.range)) {
    return { min: filter.range[0], max: filter.range[1] }
  }

  if (isRealRange(fetchedFilter?.range)) {
    return { min: fetchedFilter.range[0], max: fetchedFilter.range[1] }
  }

  return null
}

export function FilterInputSlider({ filter, onFilter, onRemove, fetchedFilter, activeFilters }) {
  const bounds = getSliderBounds(filter, fetchedFilter)
  const step = Math.max(Math.floor(filter.step ?? 1), 1)
  const activeRange = parseActiveRange(filter.field, activeFilters)

  if (!bounds) return null

  const handleChange = (value) => {
    const filterValue = `${filter.field}:${value.min}${RANGE_INDICATOR}${value.max}`

    if (value.min <= bounds.min && value.max >= bounds.max) {
      const removeFilter = onRemove || onFilter
      removeFilter(filterValue)
      return
    }

    onFilter(filterValue)
  }

  return (
    <div class="flex w-full flex-col">
      <RangeSlider
        min={bounds.min}
        max={bounds.max}
        step={step}
        format={filter.format}
        activeRange={activeRange}
        onChange={handleChange}
        isActive={Boolean(activeRange) || fetchedFilter?.facet_active > 0}
        field={filter.field}
      />
    </div>
  )
}

const RangeSlider = ({ min, max, step, onChange, format, isActive, field, activeRange }) => {
  const [minValue, setMinValue] = useState(activeRange ? activeRange[0] : min)
  const [maxValue, setMaxValue] = useState(activeRange ? activeRange[1] : max)

  useEffect(() => {
    if (activeRange) {
      setMinValue(activeRange[0])
      setMaxValue(activeRange[1])
    } else if (!isActive) {
      setMinValue(min)
      setMaxValue(max)
    }
  }, [isActive, min, max, activeRange?.[0], activeRange?.[1]])

  // Create a debounced version of onChange that only fires after 300ms of inactivity
  const debouncedOnChange = useCallback(
    debounce((values) => {
      onChange(values)
    }, 300),
    [onChange]
  )

  const handleMinChange = (event) => {
    const newMinVal = Math.min(+event.target.value, maxValue - step)
    setMinValue(newMinVal)

    // Call the debounced function instead of onChange directly
    debouncedOnChange({ min: newMinVal, max: maxValue })
  }

  const handleMaxChange = (event) => {
    const newMaxVal = Math.max(+event.target.value, minValue + step)
    setMaxValue(newMaxVal)

    // Call the debounced function instead of onChange directly
    debouncedOnChange({ min: minValue, max: newMaxVal })
  }

  // Cancel the debounced function when component unmounts
  useEffect(() => {
    return () => {
      debouncedOnChange.cancel()
    }
  }, [debouncedOnChange])

  const minPos = ((minValue - min) / (max - min)) * 100
  const maxPos = ((maxValue - min) / (max - min)) * 100
  const thumbStyle =
    'absolute top-1/2 w-[--thumb-size] h-[--thumb-size] bg-brand-primary rounded-full -translate-x-1/2 -translate-y-1/2 z-2'
  const labelStyle = 'text-s'
  const inputStyle = 'range absolute size-full p-0 opacity-0 appearance-none pointer-events-none z-3'

  const formatLabelValue = (value) => {
    if (isPriceFilterField(field)) {
      return formatPriceValue(value)
    }
    return format ? formatValue(value, format) : value
  }

  return (
    <div className="flex w-full flex-col gap-y-1">
      <div className="flex items-center justify-between">
        <p className={labelStyle}>
          Min <span className="text-type-black-secondary">{formatLabelValue(min)}</span>
        </p>
        <p className={labelStyle}>
          Max <span className="text-type-black-secondary">{formatLabelValue(max)}</span>
        </p>
      </div>
      <div className="px-1.25 md:px-0.625">
        <div className="relative mx-[calc(var(--thumb-size)_/_-2)] flex h-[--thumb-size] items-center">
          <div className="absolute mx-[calc(var(--thumb-size)_/_-2)] h-[--thumb-size] w-[calc(100%_+_var(--thumb-size))]">
            <input
              className={inputStyle}
              type="range"
              value={minValue}
              min={min}
              max={max}
              step={step}
              onChange={handleMinChange}
              aria-label="Min"
            />
            <input
              className={inputStyle}
              type="range"
              value={maxValue}
              min={min}
              max={max}
              step={step}
              onChange={handleMaxChange}
              aria-label="Max"
            />
          </div>
          <div className="absolute h-[--thumb-size] w-full">
            <div className={thumbStyle} style={{ left: `${minPos}%` }} />
            <div className="absolute top-1/2 z-0 h-0.125 w-full -translate-y-1/2 bg-page-secondary">
              <div
                className="absolute h-full bg-brand-primary"
                style={{ left: `${minPos}%`, right: `${100 - maxPos}%` }}
              />
            </div>
            <div className={thumbStyle} style={{ left: `${maxPos}%` }} />
          </div>
        </div>
      </div>
    </div>
  )
}
